import React from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  IconButton,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Typography,
  Grid,
  Chip,
  OutlinedInput,
  Alert,
  FormHelperText
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import CategoryService from '../../../services/category.service';
import EventService from '../../../services/event.service';
import SalePointService from '../../../services/salePoint.service';
import TicketService from '../../../services/ticket.service';

const styles = {
  dialog: {
    '& .MuiDialog-paper': {
      borderRadius: '12px',
      minWidth: '500px'
    }
  },
  dialogTitle: {
    display: 'flex', 
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 24px',
    backgroundColor: '#f8f9fa',
    borderBottom: '1px solid #e0e0e0'
  },
  form: {
    display: 'flex',
    flexDirection: 'column', 
    gap: '20px',
    padding: '24px'
  },
  actions: {
    padding: '16px 24px',
    borderTop: '1px solid #e0e0e0'
  },
  submitButton: {
    backgroundColor: 'primary.main',
    borderRadius: '8px',
    textTransform: 'none',
    '&:hover': {
      backgroundColor: '#333'
    }
  },
  cancelButton: {
    color: '#666',
    borderRadius: '8px',
    textTransform: 'none'
  },
  imagePreview: {
    width: '100%',
    maxWidth: '250px',
    height: '150px',
    objectFit: 'cover',
    borderRadius: '8px',
    border: '1px solid #e0e0e0'
  },
  chips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '5px'
  }
};

function Form({ open, onClose, initialData = null, onSubmit }) {
  const [formData, setFormData] = React.useState({
    name: '',
    description: '',
    price: '',
    tax: 'A',
    event_id: '',
    category_id: '',
    sale_points: {},
    image: '',
    show_order: 0,
    is_goodie: false,
    associated_tickets: [],
    status: 'visible'
  });

  const [categories, setCategories] = React.useState([]);
  const [events, setEvents] = React.useState([]);
  const [salePoints, setSalePoints] = React.useState([]);
  const [tickets, setTickets] = React.useState([]);
  const [errors, setErrors] = React.useState({});
  const [generalError, setGeneralError] = React.useState(null);

  React.useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [categoriesData, eventsData, salePointsData, ticketsData] = await Promise.all([
        CategoryService.list(),
        EventService.list(),
        SalePointService.list(),
        TicketService.list()
      ]);
      setCategories(categoriesData);
      setEvents(eventsData);
      setSalePoints(salePointsData);
      setTickets(ticketsData);
    } catch (error) {
      console.error('Erreur lors du chargement des données:', error);
    }
  };

  React.useEffect(() => {
    if (initialData) {
      const salePointsQuantities = {};
      initialData.product_sale_points?.forEach(sp => {
        salePointsQuantities[sp.salePoint.id] = sp.quantity;
      });

      setFormData({
        name: initialData.name || '',
        description: initialData.description || '',
        price: initialData.price || '',
        tax: initialData.tax || 'A',
        event_id: initialData.event?.id || '',
        category_id: initialData.category?.id || '',
        sale_points: salePointsQuantities,
        image: '',
        show_order: initialData.show_order || 0,
        is_goodie: initialData.is_goodie || false,
        associated_tickets: initialData.associatedTickets?.map(t => t.id) || [],
        status: initialData.status || 'visible'
      });
    } else {
      const emptySalePoints = {};
      salePoints.forEach(sp => {
        emptySalePoints[sp.id] = '';
      });

      setFormData({
        name: '',
        description: '',
        price: '',
        event_id: '',
        category_id: '',
        sale_points: emptySalePoints,
        image: '',
        show_order: 0,
        is_goodie: false,
        associated_tickets: [],
        status: 'visible'
      });
    }
    // Réinitialiser les erreurs quand les données changent
    setErrors({});
    setGeneralError(null);
  }, [initialData, salePoints]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Effacer l'erreur du champ quand l'utilisateur commence à taper
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleSalePointChange = (salePointId, quantity) => {
    setFormData(prev => ({
      ...prev,
      sale_points: {
        ...prev.sale_points,
        [salePointId]: quantity
      }
    }));
    // Effacer l'erreur du champ sale_points si elle existe
    if (errors.sale_points) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.sale_points;
        return newErrors;
      });
    }
  };

  const handleCheckboxChange = (event) => {
    const { name, checked } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: checked
    }));
  };

  const handleImageChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({
          ...prev,
          image: reader.result
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    // Réinitialiser les erreurs
    setErrors({});
    setGeneralError(null);
    
    // Créer un callback pour recevoir les erreurs
    const errorCallback = (validationErrors) => {
      const errorsObj = {};
      let generalMessage = null;
      
      if (validationErrors && validationErrors.length > 0) {
        validationErrors.forEach(error => {
          if (error.field) {
            errorsObj[error.field] = error.message;
          } else {
            generalMessage = error.message || 'Une erreur est survenue';
          }
        });
      }
      
      setErrors(errorsObj);
      setGeneralError(generalMessage);
    };
    
    try {
      const salePointsArray = Object.entries(formData.sale_points)
        .filter(([_, quantity]) => quantity !== '')
        .map(([id, quantity]) => ({
          id: parseInt(id),
          quantity: parseInt(quantity)
        }));

      const cleanedFormData = {
        name: formData.name,
        description: formData.description,
        price: parseFloat(formData.price),
        tax: formData.tax,
        event_id: parseInt(formData.event_id),
        category_id: parseInt(formData.category_id),
        sale_points: salePointsArray,
        image: formData.image,
        show_order: parseInt(formData.show_order),
        is_goodie: formData.is_goodie,
        associated_tickets: formData.associated_tickets,
        status: formData.status
      };
      
      if (onSubmit) {
        await onSubmit(cleanedFormData, errorCallback);
      }
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      sx={styles.dialog}
    >
      <DialogTitle sx={styles.dialogTitle}>
        <span>{initialData ? 'Modifier le produit' : 'Nouveau produit'}</span>
        <IconButton 
          onClick={onClose}
          size="small"
          sx={{ color: '#666' }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent sx={styles.form}>
          {generalError && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: '8px' }}>
              {generalError}
            </Alert>
          )}
          
          <Box sx={{ mb: 2 }}>
            {(formData.image || initialData?.image) && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Image actuelle
                </Typography>
                <img 
                  src={formData.image || `${process.env.REACT_APP_BASE_URL}/${initialData.image}`}
                  alt="Aperçu du produit"
                  style={styles.imagePreview}
                />
              </Box>
            )}
            <TextField
              fullWidth
              type="file"
              label="Image du produit"
              onChange={handleImageChange}
              size="small"
              InputLabelProps={{ shrink: true }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px'
                }
              }}
            />
          </Box>

          <TextField
            fullWidth
            label="Nom"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            size="small"
            error={!!errors.name}
            helperText={errors.name}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px'
              }
            }}
          />
          
          <TextField
            fullWidth
            label="Description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            required
            multiline
            rows={4}
            size="small"
            error={!!errors.description}
            helperText={errors.description}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px'
              }
            }}
          />

          <TextField
            fullWidth
            label="Prix"
            name="price"
            type="number"
            value={formData.price}
            onChange={handleChange}
            required
            size="small"
            error={!!errors.price}
            helperText={errors.price}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px'
              }
            }}
          />

          <TextField
            fullWidth
            label="Ordre d'affichage"
            name="show_order"
            type="number"
            value={formData.show_order}
            onChange={handleChange}
            size="small"
            error={!!errors.show_order}
            helperText={errors.show_order || "Numéro pour définir l'ordre d'affichage du produit (0 = premier)"}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px'
              }
            }}
          />

          <FormControl fullWidth size="small" error={!!errors.category_id}>
            <InputLabel id="category-label">Catégorie</InputLabel>
            <Select
              labelId="category-label"
              name="category_id"
              value={formData.category_id}
              onChange={handleChange}
              required
              label="Catégorie"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px'
                }
              }}
            >
              {categories.map((category) => (
                <MenuItem key={category.id} value={category.id}>
                  {category.name}
                </MenuItem>
              ))}
            </Select>
            {errors.category_id && <FormHelperText>{errors.category_id}</FormHelperText>}
          </FormControl>

          <FormControl fullWidth size="small" error={!!errors.event_id}>
            <InputLabel id="event-label">Événement</InputLabel>
            <Select
              labelId="event-label"
              name="event_id"
              value={formData.event_id}
              onChange={handleChange}
              required
              label="Événement"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px'
                }
              }}
            >
              {events.map((event) => (
                <MenuItem key={event.id} value={event.id}>
                  {event.name}
                </MenuItem>
              ))}
            </Select>
            {errors.event_id && <FormHelperText>{errors.event_id}</FormHelperText>}
          </FormControl>

          <FormControl fullWidth size="small" error={!!errors.status}>
            <InputLabel id="status-label">Statut</InputLabel>
            <Select
              labelId="status-label"
              name="status"
              value={formData.status}
              onChange={handleChange}
              required
              label="Statut"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px'
                }
              }}
            >
              <MenuItem value="visible">Visible</MenuItem>
              <MenuItem value="hidden">Masqué</MenuItem>
            </Select>
            {errors.status && <FormHelperText>{errors.status}</FormHelperText>}
          </FormControl>

          <Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Points de vente
            </Typography>
            <Grid container spacing={2}>
              {salePoints.map((salePoint) => (
                <Grid item xs={12} key={salePoint.id} sx={{mb: 1}}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={6}>
                      <Typography>{salePoint.name}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        type="number"
                        size="small"
                        placeholder="Quantité"
                        value={formData.sale_points[salePoint.id]}
                        onChange={(e) => handleSalePointChange(salePoint.id, e.target.value)}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '8px'
                          }
                        }}
                      />
                    </Grid>
                  </Grid>
                </Grid>
              ))}
            </Grid>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <input
              type="checkbox"
              id="is_goodie"
              name="is_goodie"
              checked={formData.is_goodie}
              onChange={handleCheckboxChange}
              style={{ width: '18px', height: '18px' }}
            />
            <label htmlFor="is_goodie" style={{ fontSize: '14px', color: '#666' }}>
              goodies offert
            </label>
          </Box>

          {formData.is_goodie && (
            <FormControl fullWidth size="small" error={!!errors.associated_tickets}>
              <InputLabel>Tickets associés</InputLabel>
              <Select
                multiple
                name="associated_tickets"
                value={formData.associated_tickets}
                onChange={handleChange}
                input={<OutlinedInput label="Tickets associés" />}
                renderValue={(selected) => (
                  <Box sx={styles.chips}>
                    {selected.map((value) => {
                      const ticket = tickets.find(t => t.id === value);
                      return (
                        <Chip 
                          key={value} 
                          label={ticket?.name} 
                          size="small"
                        />
                      );
                    })}
                  </Box>
                )}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px'
                  }
                }}
              >
                {tickets.map((ticket) => (
                  <MenuItem key={ticket.id} value={ticket.id}>
                    {ticket.name}
                  </MenuItem>
                ))}
              </Select>
              {errors.associated_tickets && <FormHelperText>{errors.associated_tickets}</FormHelperText>}
            </FormControl>
          )}
        </DialogContent>

        <DialogActions sx={styles.actions}>
          <Button 
            onClick={onClose}
            sx={styles.cancelButton}
          >
            Annuler
          </Button>
          <Button 
            type="submit"
            variant="contained"
            sx={styles.submitButton}
          >
            {initialData ? 'Modifier' : 'Ajouter'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

Form.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  initialData: PropTypes.shape({
    name: PropTypes.string,
    description: PropTypes.string,
    price: PropTypes.number,
    event: PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string
    }),
    category: PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string
    }),
    product_sale_points: PropTypes.arrayOf(PropTypes.shape({
      salePoint: PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string
      }),
      quantity: PropTypes.number
    })),
    image: PropTypes.string,
    show_order: PropTypes.number,
    tax: PropTypes.string,
    is_goodie: PropTypes.bool,
    associatedTickets: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      description: PropTypes.string,
      ticket_type: PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string
      }),
      price: PropTypes.number,
      quantity: PropTypes.number,
      image: PropTypes.string,
      show_order: PropTypes.number
    }))
  })
};

export default Form;